import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFilterRule, getFolders, MessageDetail } from '../api/mailbox';

function extractEmail(raw: string): string {
  const match = raw.match(/<([^>]+)>/);
  if (match) return match[1].trim();
  return raw.trim();
}

export interface FilterDraft {
  name: string;
  condition_field: string;
  condition_value: string;
  action: string;
  action_folder: string | number;
  is_active: boolean;
}

interface FilterRuleDialogProps {
  open: boolean;
  onClose: () => void;
  message?: Pick<
    MessageDetail,
    'from_address' | 'to_addresses' | 'cc_addresses' | 'subject'
  > | null;
  initial?: Partial<FilterDraft>;
}

export default function FilterRuleDialog({
  open,
  onClose,
  message,
  initial,
}: FilterRuleDialogProps) {
  const qc = useQueryClient();
  const fromEmail = message ? extractEmail(message.from_address || '') : '';

  const [form, setForm] = useState<FilterDraft>(() => ({
    name: initial?.name || (fromEmail ? `From ${fromEmail}` : 'New filter'),
    condition_field: initial?.condition_field || 'from_contains',
    condition_value: initial?.condition_value || fromEmail,
    action: initial?.action || 'move',
    action_folder: initial?.action_folder || '',
    is_active: initial?.is_active ?? true,
  }));
  const [error, setError] = useState('');

  // Reset when opened with a new message
  React.useEffect(() => {
    if (!open) return;
    const email = message ? extractEmail(message.from_address || '') : '';
    setForm({
      name: initial?.name || (email ? `From ${email}` : 'New filter'),
      condition_field: initial?.condition_field || 'from_contains',
      condition_value: initial?.condition_value || email,
      action: initial?.action || 'move',
      action_folder: initial?.action_folder || '',
      is_active: initial?.is_active ?? true,
    });
    setError('');
  }, [open, message, initial]);

  const { data: foldersData } = useQuery({
    queryKey: ['folders'],
    queryFn: () => getFolders({ page_size: 5000 }),
    enabled: open,
  });

  const folders = foldersData?.results || [];

  const presets = useMemo(() => {
    if (!message) return [];
    return [
      {
        label: 'From sender',
        condition_field: 'from_contains',
        condition_value: extractEmail(message.from_address || ''),
      },
      {
        label: 'To recipient',
        condition_field: 'to_contains',
        condition_value: extractEmail(message.to_addresses || ''),
      },
      {
        label: 'Cc contains',
        condition_field: 'cc_contains',
        condition_value: extractEmail(message.cc_addresses || ''),
      },
      {
        label: 'Subject contains',
        condition_field: 'subject_contains',
        condition_value: (message.subject || '').replace(/^(re|fwd?):\s*/i, '').trim(),
      },
    ].filter((p) => p.condition_value);
  }, [message]);

  const createMutation = useMutation({
    mutationFn: () =>
      createFilterRule({
        name: form.name,
        condition_field: form.condition_field,
        condition_value: form.condition_value,
        action: form.action,
        action_folder:
          form.action === 'move' && form.action_folder ? Number(form.action_folder) : null,
        is_active: form.is_active,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filter-rules'] });
      onClose();
    },
    onError: (err: any) => setError(JSON.stringify(err?.response?.data || 'Error')),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create filter</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          {presets.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {presets.map((p) => (
                <Button
                  key={p.label}
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      name: `${p.label}: ${p.condition_value}`.slice(0, 80),
                      condition_field: p.condition_field,
                      condition_value: p.condition_value,
                    }))
                  }
                >
                  {p.label}
                </Button>
              ))}
            </Stack>
          )}
          <TextField
            label="Name"
            size="small"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            fullWidth
          />
          <TextField
            select
            label="Match"
            size="small"
            value={form.condition_field}
            onChange={(e) => setForm((p) => ({ ...p, condition_field: e.target.value }))}
            fullWidth
          >
            <MenuItem value="from_contains">From contains</MenuItem>
            <MenuItem value="to_contains">To contains</MenuItem>
            <MenuItem value="cc_contains">Cc contains</MenuItem>
            <MenuItem value="subject_contains">Subject contains</MenuItem>
            <MenuItem value="body_contains">Body contains</MenuItem>
            <MenuItem value="keyword">Keyword (headers + body)</MenuItem>
            <MenuItem value="has_attachment">Has attachment</MenuItem>
          </TextField>
          {form.condition_field !== 'has_attachment' && (
            <TextField
              label="Value"
              size="small"
              value={form.condition_value}
              onChange={(e) => setForm((p) => ({ ...p, condition_value: e.target.value }))}
              fullWidth
              helperText="Customize freely — any substring match"
            />
          )}
          <TextField
            select
            label="Action"
            size="small"
            value={form.action}
            onChange={(e) => setForm((p) => ({ ...p, action: e.target.value }))}
            fullWidth
          >
            <MenuItem value="move">Move to folder</MenuItem>
            <MenuItem value="mark_read">Mark as read</MenuItem>
            <MenuItem value="mark_starred">Mark as starred</MenuItem>
            <MenuItem value="delete">Delete</MenuItem>
            <MenuItem value="archive">Archive</MenuItem>
          </TextField>
          {form.action === 'move' && (
            <TextField
              select
              label="Destination folder"
              size="small"
              value={form.action_folder}
              onChange={(e) => setForm((p) => ({ ...p, action_folder: e.target.value }))}
              fullWidth
            >
              {folders.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.imap_name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => createMutation.mutate()}
          disabled={!form.name || createMutation.isPending}
        >
          {createMutation.isPending ? <CircularProgress size={20} /> : 'Save filter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
